import { DefaultVatRateOption } from "../types/VatCode.ts";
import Form from "../../../components/form-library/Form.tsx";
import type { FormInstance } from "../../../components/form-library/types.ts";
import FormDropdown from "../../../components/general/FormDropdown.tsx";
import { InlineInput } from "../../../components/general/InlineInput.tsx";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { InformationCircleIcon, TrashIcon } from "@heroicons/react/20/solid";
import { BookingProvider, isDefaultBookingProvider } from "../../../types/enums.ts";
import type { GlAccount } from "../../../hooks/useGLAccounts.ts";
import { GLASelector, GlaFilterPreset } from "../../../components/specific/GLASelector";
import { resolveSubledger, useSubledgerContext } from "../../../contexts/SubledgerContext.tsx";
import MoneyInput from '../../../components/general/MoneyInput/index.ts';
import { ConvertedAmount } from './ConvertedAmount.tsx';
import type { MonetaryOrigin } from '../../../components/general/ForeignAmountLabel.tsx';
import { Money } from '../../../lib/Money.ts';
import { cn } from '../../../lib/utils.ts';
import { fieldVariants } from '../../../components/general/fieldStyles.ts';
import { lineItemFieldName } from '../utils/lineItemsHelper.ts';
import { LineItemTransitoryScheduleField } from './LineItemTransitoryScheduleField.tsx';
import type {CurrencyEnum, DocumentDirection} from '../../../generated/enums.ts';

type DeleteRowButtonProps = {
    onDelete: () => void;
}

export type LineItemRowAttentionData = {
    requiresAttention: boolean;
    attentionMessage: string | null;
};

export type LineCell = 'description' | 'net';

const CELL = 'px-1.5 py-2 align-top [&_.form-item>div>p]:mt-1 [&_.form-item>div>p]:text-[11px] [&_.form-item>div>p]:leading-4';
const CELL_INPUT = 'h-8 px-2 text-[12.5px] hover:border-gray-400 aria-[invalid=true]:border-red-400';

export function DeleteRowButton({ onDelete }: DeleteRowButtonProps) {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      onDelete();
    };
  
    return (
      <button
        type="button"
        aria-label="Regel verwijderen"
        title="Regel verwijderen"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
        onClick={handleClick}
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    );
  }


  type LineItemFormRowProps = {   
    itemId: string
    index: number
    form: FormInstance
    glAccounts: GlAccount[]
    documentDirection: DocumentDirection
    bookingProvider: BookingProvider
    attentionData?: LineItemRowAttentionData
    defaultVatRateOptions: DefaultVatRateOption[]
    currency?: CurrencyEnum
    billedNetAmount?: Money | null
    billedVatAmount?: Money | null
    monetaryOrigin?: MonetaryOrigin | null
    shouldShowBookingError: boolean
    onDelete: () => void
    onVatRateChange?: (itemId: string, vatRatePercentage: number | null) => void
    onNetAmountChange?: (itemId: string, netAmount: Money | null) => void
}

export function LineItemsRow({
    itemId,
    index,
    form,
    glAccounts,
    documentDirection,
    defaultVatRateOptions,
    bookingProvider,
    attentionData,
    currency = 'EUR',
    billedNetAmount,
    billedVatAmount,
    monetaryOrigin,
    shouldShowBookingError,
    onDelete,
    onVatRateChange,
    onNetAmountChange,
}: LineItemFormRowProps) {
    const { subledgers, canUseSubledgers } = useSubledgerContext();
    const hasDefaultBooking = isDefaultBookingProvider(bookingProvider);
    const canBook = hasDefaultBooking;
    const requiresAttention = attentionData?.requiresAttention ?? false;
    const attentionMessage = attentionData?.attentionMessage ?? null;
    const hasAttentionMessage = requiresAttention && Boolean(attentionMessage);
    const categoryFieldName = lineItemFieldName(itemId, 'category_id');
    const subledgerFieldName = lineItemFieldName(itemId, 'subledger_id');
    const subledgerId = form.getFieldValue(subledgerFieldName) as string | null | undefined;
    const selectedSubledger = resolveSubledger(subledgerId, subledgers);
    const rowLabel = `Regel ${index + 1}`;

    return (
        <>
        <tr
            data-line-id={itemId}
            className={cn(
                'group bg-white transition-colors focus-within:bg-primary/[0.035] hover:bg-gray-50/80',
                hasAttentionMessage && 'bg-amber-100/80 hover:bg-amber-100',
            )}
        >
            <td className={cn(CELL, 'w-10 px-1 text-center')}>
                <span
                    className={cn(
                        'inline-flex h-8 items-center justify-center text-[11px] font-semibold tabular-nums',
                        hasAttentionMessage
                            ? 'text-amber-700'
                            : 'text-gray-500 group-focus-within:text-darkprimary',
                    )}
                    aria-label={rowLabel}
                >
                    {index + 1}
                </span>
            </td>
            {(canBook) && (
                <td className={cn(CELL, 'w-[11rem]')}>
                    <div className="w-[10rem] [&>.form-item>div>div>button]:h-8 [&>.form-item>div>div>button]:min-h-0">
                        {hasDefaultBooking && glAccounts.length > 0 ? (
                            <Form.Item
                                name={categoryFieldName}
                                noStyle
                                rules={[{ required: shouldShowBookingError, message: 'Categorie is verplicht' }]}
                            >
                                {({ value }) => (
                                    <GLASelector
                                        accounts={glAccounts}
                                        filterPreset={GlaFilterPreset.PROFIT_AND_LOSS}
                                        documentDirectionFilter={documentDirection}
                                        size="xs"
                                        value={glAccounts.find((a) => a.id === value) ?? null}
                                        onChange={(account) =>
                                            form.setFieldValue(categoryFieldName, account?.id ?? null)
                                        }
                                        placeholder="Selecteer een rekening"
                                        selectedSubledger={canUseSubledgers ? selectedSubledger : undefined}
                                        onSubledgerChange={
                                            canUseSubledgers
                                                ? (subledger) => form.setFieldValue(subledgerFieldName, subledger?.id ?? null)
                                                : undefined
                                        }
                                    />
                                )}
                            </Form.Item>
                        ) : (
                            <div className="py-1.5 text-xs text-gray-400">Geen opties</div>
                        )}
                    </div>
                </td>
            )}
            <td className={CELL} data-cell="description">
                <Form.Item
                    name={lineItemFieldName(itemId, 'name_or_description')}
                    noStyle
                >
                    <InlineInput
                        type="text"
                        placeholder="Omschrijving"
                        aria-label={`${rowLabel} omschrijving`}
                        selectOnFocus
                        className={cn(fieldVariants(), CELL_INPUT)}
                    />
                </Form.Item>
            </td>

            <td className={cn(CELL, 'w-[9rem] text-right')} data-cell="net">
                <div className="inline-flex items-center justify-end gap-1">
                    <Form.Item
                        name={lineItemFieldName(itemId, 'net_amount')}
                        noStyle
                    >
                        <MoneyInput
                            aria-label="Bedrag excl. BTW"
                            currency={currency}
                            selectOnFocus
                            allowEmpty
                            placeholder="Bedrag"
                            onChange={(money) => {
                                onNetAmountChange?.(itemId, money);
                            }}
                            className={cn(fieldVariants(), CELL_INPUT, 'text-right tabular-nums')}
                        />
                    </Form.Item>
                    {monetaryOrigin && billedNetAmount && (
                        <ConvertedAmount origin={monetaryOrigin} billedAmount={billedNetAmount} withLabel={false}/>
                    )}
                </div>
            </td>

            <td className={cn(CELL, 'w-[6.5rem]')}>
                <div
                    role="group"
                    aria-label={`${rowLabel} BTW-tarief`}
                    className="[&_button]:h-8 [&_button]:py-0 [&_button]:text-[12.5px]"
                >
                    <Form.Item
                        name={lineItemFieldName(itemId, 'vat_rate_percentage_id')}
                        noStyle
                    >
                        <FormDropdown
                            aria-label="BTW"
                            onChange={(value) => {
                                const vatRatePercentage = defaultVatRateOptions.find(opt => opt.value === value)?.percentage ?? null;
                                onVatRateChange?.(itemId, vatRatePercentage);
                            }}
                            options={defaultVatRateOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                            placeholder="BTW"
                        />
                    </Form.Item>
                </div>
            </td>

            <td className={cn(CELL, 'w-[7rem] text-right')}>
                <div className="inline-flex items-center justify-end gap-1">
                    <Form.Item
                        name={lineItemFieldName(itemId, 'vat_amount')}
                        noStyle
                        disabled={true}
                    >
                        <MoneyInput
                            aria-label="BTW bedrag"
                            currency={currency}
                            className="h-8 border-transparent bg-gray-50 px-2 text-right text-[12.5px] tabular-nums disabled:text-gray-600 disabled:opacity-100"
                            disabled
                        />
                    </Form.Item>
                    {monetaryOrigin && billedVatAmount && (
                        <ConvertedAmount origin={monetaryOrigin} billedAmount={billedVatAmount} withLabel={false}/>
                    )}
                </div>
            </td>

            <td className={cn(CELL, 'w-[4.75rem] whitespace-nowrap')}>
                <div className="flex items-center justify-end gap-1">
                    <Form.Item name={lineItemFieldName(itemId, 'transitory_schedule')} noStyle>
                        <LineItemTransitoryScheduleField />
                    </Form.Item>
                    <button
                        type="button"
                        onClick={onDelete}
                        aria-label={`${rowLabel} verwijderen`}
                        title="Regel verwijderen"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                    >
                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
            </td>
        </tr>
        {hasAttentionMessage && (
            <tr data-line-id={itemId} className="border-t border-amber-100 bg-amber-100/80">
                <td colSpan={canBook ? 7 : 6} className="px-3 py-1.5 text-[11.5px] leading-4 text-gray-600">
                    <span className="inline-flex items-start gap-1.5 pl-8">
                        <InformationCircleIcon className="mt-px h-3.5 w-3.5 shrink-0 text-gray-600" aria-hidden="true" />
                        {attentionMessage}
                    </span>
                </td>
            </tr>
        )}
        </>
    );
}
